import React, { useMemo, useState, useEffect } from "react";
import { useFormik } from "formik";
import {
  Grid,
  TextField,
  MenuItem,
  Alert,
  Box,
  Typography,
} from "@mui/material";
import { FormDialog } from "../../../components/common/FormDialog/FormDialog";
import {
  createAbsenceRequestValidationSchema,
  CreateAbsenceRequestFormValues,
} from "../validation/absenceRequestValidationSchema";
import { useCreateAbsenceRequestMutation } from "../../../api/absenceRequestsApi";
import { useGetMyAbsenceBalanceQuery } from "../../../api/absenceBalancesApi";
import {
  AbsenceCategory,
  AbsenceCategoryNames,
  getAbsenceCategoryColor,
} from "../../../constants/absenceEnums";
import { calculateBusinessDays, calculateTotalDays } from "../../../utils/absenceUtils";
import { extractAllErrors } from "../../../utils/errorUtils";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import WarningIcon from "@mui/icons-material/Warning";
import WeekendIcon from "@mui/icons-material/Weekend";
import { Chip, Tooltip } from "@mui/material";

interface CreateAbsenceRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreateAbsenceRequestDialog: React.FC<
  CreateAbsenceRequestDialogProps
> = ({ open, onClose, onSuccess }) => {
  const [createAbsenceRequest, { isLoading: isMutationLoading, reset }] =
    useCreateAbsenceRequestMutation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setIsSubmitting(false);
      setValidationErrors([]);
      reset();
    }
  }, [open, reset]);
  
  // Get employee balance to extract employeeId
  const { data: balance } = useGetMyAbsenceBalanceQuery();

  const formik = useFormik<CreateAbsenceRequestFormValues>({
    initialValues: {
      absenceCategory: null,
      startDate: null,
      endDate: null,
      requestReason: "",
    },
    validationSchema: createAbsenceRequestValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!balance?.employeeId) {
        console.error("Employee ID not available");
        return;
      }

      setIsSubmitting(true);
      setValidationErrors([]);
      try {
        await createAbsenceRequest({
          employeeId: balance.employeeId,
          absenceCategory: values.absenceCategory!,
          startDate: values.startDate!,
          endDate: values.endDate!,
          requestReason: values.requestReason,
          notes: undefined,
          bypassValidation: false,
        }).unwrap();

        formik.resetForm();
        setIsSubmitting(false);
        onClose();
        onSuccess?.();
      } catch (err: any) {
        setIsSubmitting(false);
        const errors = extractAllErrors(err);
        setValidationErrors(errors.length > 0 ? errors : ["An unexpected error occurred. Please try again."]);
      }
    },
  });

  const worksWeekends = balance?.worksWeekends ?? false;

  const businessDays = useMemo(() => {
    if (formik.values.startDate && formik.values.endDate) {
      return calculateBusinessDays(formik.values.startDate, formik.values.endDate);
    }
    return 0;
  }, [formik.values.startDate, formik.values.endDate]);

  const totalDays = useMemo(() => {
    if (formik.values.startDate && formik.values.endDate) {
      return calculateTotalDays(formik.values.startDate, formik.values.endDate);
    }
    return 0;
  }, [formik.values.startDate, formik.values.endDate]);

  const daysToDeduct = worksWeekends ? totalDays : businessDays;

  const handleClose = () => {
    formik.resetForm();
    setValidationErrors([]);
    setIsSubmitting(false);
    onClose();
  };

  // Get available absence categories for employee requests
  // FamilyHoliday is excluded - it's created automatically by the system
  const availableCategories = useMemo(() => {
    return [
      AbsenceCategory.Vacation,
      AbsenceCategory.SickDays,
      AbsenceCategory.MedicalLeave,
      AbsenceCategory.Maternity,
      AbsenceCategory.Parental,
      AbsenceCategory.EarlyLeave,
      AbsenceCategory.LateOnShift,
    ];
  }, []);


  return (
    <FormDialog
      open={open}
      title="Request Absence"
      onClose={handleClose}
      onSubmit={formik.handleSubmit}
      isLoading={isSubmitting || formik.isSubmitting}
      submitLabel="Submit Request"
      maxWidth="sm"
    >
      <Grid container spacing={3}>
        {/* Validation errors */}
        {validationErrors.length > 0 && (
          <Grid item xs={12}>
            <Alert severity="warning" icon={<WarningIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Cannot submit request
              </Typography>
              <Box>
                {validationErrors.map((msg, index) => (
                  <Box key={index} sx={{ mb: index < validationErrors.length - 1 ? 0.5 : 0 }}>
                    • {msg}
                  </Box>
                ))}
              </Box>
            </Alert>
          </Grid>
        )}

        {/* Absence Category */}
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            label="Absence Type"
            name="absenceCategory"
            value={formik.values.absenceCategory || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.absenceCategory &&
              Boolean(formik.errors.absenceCategory)
            }
            helperText={
              formik.touched.absenceCategory && formik.errors.absenceCategory
            }
            required
            size="small"
          >
            {availableCategories.map((category) => (
              <MenuItem key={category} value={category}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: getAbsenceCategoryColor(category),
                    }}
                  />
                  {AbsenceCategoryNames[category]}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Date Range */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="date"
            label="Start Date"
            name="startDate"
            value={formik.values.startDate ? formik.values.startDate.split('T')[0] : ''}
            onChange={(e) => {
              const value = e.target.value;
              formik.setFieldValue('startDate', value || null);
            }}
            onBlur={formik.handleBlur}
            error={formik.touched.startDate && Boolean(formik.errors.startDate)}
            helperText={formik.touched.startDate && formik.errors.startDate}
            required
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="date"
            label="End Date"
            name="endDate"
            value={formik.values.endDate ? formik.values.endDate.split('T')[0] : ''}
            onChange={(e) => {
              const value = e.target.value;
              formik.setFieldValue('endDate', value || null);
            }}
            onBlur={formik.handleBlur}
            error={formik.touched.endDate && Boolean(formik.errors.endDate)}
            helperText={formik.touched.endDate && formik.errors.endDate}
            required
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        {/* Works Weekends indicator */}
        {worksWeekends && (
          <Grid item xs={12}>
            <Tooltip title="Your absence days are calculated including weekends (Saturday & Sunday)." placement="top">
              <Chip
                icon={<WeekendIcon />}
                label="Works Weekends — weekend days count toward balance"
                size="small"
                sx={{
                  backgroundColor: "#FF8F00",
                  color: "#fff",
                  fontWeight: 500,
                  "& .MuiChip-icon": { color: "#fff" },
                }}
              />
            </Tooltip>
          </Grid>
        )}

        {/* Days Display */}
        {formik.values.startDate && formik.values.endDate && daysToDeduct > 0 && (
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 2,
                backgroundColor: "primary.light",
                borderRadius: 1,
                color: "primary.contrastText",
              }}
            >
              <CalendarTodayIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {daysToDeduct} day{daysToDeduct !== 1 ? "s" : ""} requested
                {worksWeekends && totalDays !== businessDays && (
                  <span style={{ opacity: 0.8, marginLeft: 6, fontSize: "0.85em" }}>
                    (including weekends)
                  </span>
                )}
                {!worksWeekends && (
                  <span style={{ opacity: 0.8, marginLeft: 6, fontSize: "0.85em" }}>
                    (business days only)
                  </span>
                )}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Reason */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason"
            name="requestReason"
            value={formik.values.requestReason}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.requestReason &&
              Boolean(formik.errors.requestReason)
            }
            helperText={
              formik.touched.requestReason && formik.errors.requestReason
            }
            required
            size="small"
            placeholder="Please provide a reason for your absence request..."
          />
        </Grid>

      </Grid>
    </FormDialog>
  );
};

